import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STARTER = "g'day! tell me about your project in one or two sentences — what are you building, and where?";

export default function Estimator() {
  const [sessionId] = useState(() => `est-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: STARTER }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/estimator`, { session_id: sessionId, message: text });
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", text: "sorry — the estimator hit a snag. please try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  const quick = ["new family home, 300m², adelaide", "small commercial fit-out, melbourne", "industrial shed 3000m², perth", "renovation/extension, sydney"];

  return (
    <section id="estimator" data-testid="section-estimator" className="bg-bone py-24 md:py-40">
      <div className="w-full max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-12 gap-8">
        <p className="label col-span-12 md:col-span-2 reveal">05 — Estimator</p>
        <div className="col-span-12 md:col-span-9 reveal">
          <h2 className="display text-[9vw] md:text-6xl lg:text-[5rem] leading-[0.98] tracking-tighter">
            Talk to our <span className="italic text-rust font-light">AI estimator.</span>
          </h2>
          <p className="mt-6 text-lg text-muted2 max-w-2xl">
            A short conversation that returns an honest ballpark on scope, fees and timeline — before you talk to a human. Indicative only; final figures come from a proper consult.
          </p>
        </div>

        <div className="col-span-12 mt-10 reveal">
          <div className="bg-ink text-bone p-6 md:p-8 grain relative">
            <div className="flex items-center justify-between mb-6">
              <p className="label text-bone/50 font-mono">virarc.estimator — session {sessionId.slice(-6)}</p>
              <span className="text-bone/40 text-xs font-mono">{busy ? "thinking…" : "ready"}<span className="ml-1 inline-block w-2 h-3 bg-clay align-middle animate-blink" /></span>
            </div>

            <div className="space-y-5 max-h-[420px] overflow-y-auto pr-2 no-scrollbar" data-testid="estimator-thread">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] md:max-w-[70%] px-5 py-4 text-[15px] leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-clay text-ink"
                        : "bg-bone/5 border border-bone/15 text-bone"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex">
                  <div className="px-5 py-4 bg-bone/5 border border-bone/15 text-bone/70 text-sm">writing…</div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {quick.map((q) => (
                <button
                  key={q}
                  type="button"
                  data-testid={`estimator-quick-${q.slice(0, 6)}`}
                  onClick={() => setInput(q)}
                  className="text-[11px] uppercase tracking-[0.2em] border border-bone/20 px-3 py-1.5 hover:bg-bone hover:text-ink transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>

            <form onSubmit={send} className="mt-5 flex items-center gap-3 border-t border-bone/15 pt-5">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="describe your project, location, scope…"
                data-testid="estimator-input"
                className="flex-1 bg-transparent border-b border-bone/30 focus:border-clay outline-none text-bone py-2 placeholder:text-bone/40"
              />
              <button
                type="submit"
                data-testid="estimator-send"
                disabled={busy || !input.trim()}
                className="px-5 py-2.5 bg-clay text-ink text-[12px] uppercase tracking-[0.22em] disabled:opacity-40 hover:bg-bone transition-colors"
              >
                Send →
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
